import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { getSocket } from '../../services/socket'
import toast from 'react-hot-toast'
import ConfigBot from './ConfigBot'
import OneTimeSecretDialog from '../../components/OneTimeSecretDialog'
import { useAuth } from '../../context/AuthContext'
import SelectorColor from '../../components/SelectorColor'

// La paleta y el panel de "más colores" viven en SelectorColor

const STATUS_LABEL = { conectado: 'Conectado', conectando: 'Conectando', desconectado: 'Desconectado', baneado: 'Baneado' }
const STATUS_COLOR = { conectado: '#22c55e', conectando: '#f59e0b', desconectado: '#94a3b8', baneado: '#ef4444' }

function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: 'var(--bg)', borderRadius: 12, padding: '16px 20px', flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--primary)' }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

export default function Sesiones() {
  const { usuario, plan } = useAuth()
  const puedeVerWebhooks = ['admin', 'supervisor'].includes(usuario?.rol) && plan?.permiteWebhooks
  const [sesiones, setSesiones]   = useState([])
  const [opciones, setOpciones]   = useState({ pipelines: [], negocios: [] })
  const [editando, setEditando]   = useState(null)   // sesión en edición
  const [form, setForm]           = useState({})
  const [stats, setStats]         = useState(null)
  const [tab, setTab]             = useState('info')
  const [showNuevo, setShowNuevo] = useState(false)
  const [nuevo, setNuevo]         = useState({ nombre: '', color: '#25D366' })
  const [creando, setCreando]     = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [isMobile, setIsMobile]   = useState(false)
  const [celulares, setCelulares] = useState([])
  const [showCelulares, setShowCelulares] = useState(false)
  const navigate = useNavigate()

  // Detectar mobile y escuchar resize (mismo patrón que Inbox)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Los celulares se piden aparte y sin bloquear: son documentación de apoyo,
  // así que si esa llamada falla la pantalla de números debe seguir funcionando.
  const cargarCelulares = useCallback(async () => {
    try {
      const { data } = await api.get('/dispositivos')
      setCelulares(Array.isArray(data) ? data : [])
    } catch { /* sin celulares registrados o sin permiso: se muestra vacío */ }
  }, [])

  const cargar = useCallback(async () => {
    const [{ data: raw }, { data: o }] = await Promise.all([
      api.get('/sesiones'),
      api.get('/sesiones/opciones').catch(() => ({ data: { pipelines: [], negocios: [] } })),
    ])
    const s = Array.isArray(raw) ? raw : raw.sesiones
    setSesiones(s)
    setOpciones(o)
    cargarCelulares()
  }, [cargarCelulares])

  useEffect(() => { cargar() }, [cargar])

  useEffect(() => {
    const sock = getSocket()
    if (!sock) return
    const onQR = ({ sesionId, qr }) => {
      setSesiones(prev => prev.map(s => s.id === sesionId ? { ...s, qr } : s))
      setEditando(prev => prev?.id === sesionId ? { ...prev, qr } : prev)
    }
    const onUpd = ({ sesionId, status, telefono }) => {
      setSesiones(prev => prev.map(s => s.id === sesionId
        ? { ...s, status, ...(telefono ? { telefono } : {}), qr: status === 'conectado' ? null : s.qr }
        : s))
      setEditando(prev => {
        if (prev?.id !== sesionId) return prev
        return { ...prev, status, ...(telefono ? { telefono } : {}), qr: status === 'conectado' ? null : prev.qr }
      })
      setForm(prev => prev.id === sesionId
        ? { ...prev, status, ...(telefono ? { telefono } : {}) }
        : prev)
      if (status === 'conectado') toast.success('📱 Número conectado')
    }
    sock.on('qr', onQR)
    sock.on('sesion-update', onUpd)
    return () => { sock.off('qr', onQR); sock.off('sesion-update', onUpd) }
  }, [])

  // Abrir panel de edición
  const abrir = async (s) => {
    setEditando(s)
    setForm({ ...s })
    setTab('info')
    setStats(null)
    // Cargar stats en segundo plano
    api.get(`/sesiones/${s.id}/stats`).then(r => setStats(r.data)).catch(() => {})
  }

  const cerrar = () => { setEditando(null); setForm({}); setStats(null) }

  // En mobile: interceptar el botón Atrás del teléfono para volver a la lista
  // en vez de salir de la sección
  useEffect(() => {
    if (!editando || !isMobile) return
    window.history.pushState({ sesionOpen: true }, '')
    const onPop = () => cerrar()
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [editando, isMobile])

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const guardar = async () => {
    setGuardando(true)
    try {
      const { data } = await api.put(`/sesiones/${form.id}`, form)
      setSesiones(prev => prev.map(s => s.id === data.id ? { ...s, ...data } : s))
      setEditando({ ...editando, ...data })
      toast.success('✅ Número guardado')
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar') }
    finally { setGuardando(false) }
  }

  // Reconectar conserva el número: reinicia la sesión y, si el teléfono sigue
  // vinculado, vuelve sola sin escanear nada.
  const reconectar = async (id) => {
    try {
      await api.post(`/sesiones/${id}/reconectar`)
      toast('⏳ Reconectando, sin perder el número vinculado...')
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo reconectar') }
  }

  // Revincular SÍ desvincula: solo sirve para poner otro número. Se pregunta
  // antes, porque deja el WhatsApp fuera hasta que alguien escanee el QR.
  const revincular = async (s) => {
    const ok = window.confirm(
      `¿Vincular otro número en "${s.nombre}"?\n\n` +
      `Se desvinculará ${s.telefono ? '+' + s.telefono : 'el número actual'} y habrá que escanear un QR nuevo ` +
      `desde el teléfono. Mientras tanto no podrá enviar ni recibir mensajes.\n\n` +
      `Si solo quieres levantar la sesión caída, cancela y usa "Reconectar".`
    )
    if (!ok) return
    try {
      await api.post(`/sesiones/${s.id}/revincular`)
      toast('⏳ Generando QR nuevo...')
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo revincular') }
  }

  const eliminar = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.nombre}"? Se perderá la sesión de WhatsApp.`)) return
    try {
      await api.delete(`/sesiones/${s.id}`)
      setSesiones(prev => prev.filter(x => x.id !== s.id))
      if (editando?.id === s.id) cerrar()
      toast.success('Número eliminado')
    } catch (e) { toast.error(e.response?.data?.error || 'Error') }
  }

  const crear = async (e) => {
    e.preventDefault()
    if (!nuevo.nombre.trim()) return
    setCreando(true)
    try {
      const { data } = await api.post('/sesiones', nuevo)
      setSesiones(prev => [...prev, data])
      setNuevo({ nombre: '', color: '#25D366' })
      setShowNuevo(false)
      toast.success('Número creado, escanea el QR')
      abrir(data)
    } catch (e) { toast.error(e.response?.data?.error || 'Error') }
    finally { setCreando(false) }
  }

  const TABS = [
    { id: 'info',           icon: '📋', label: 'Información' },
    { id: 'conexion',       icon: '📡', label: 'Conexión' },
    { id: 'pipeline',       icon: '🔀', label: 'Pipeline & Negocio' },
    { id: 'meta',           icon: '☁️', label: 'Meta API' },
    { id: 'bot',            icon: '🤖', label: 'Chatbot' },
    { id: 'flujos',         icon: '🔀', label: 'Flujos' },
    { id: 'integraciones',  icon: '🔌', label: 'Integraciones' },
    { id: 'kb',             icon: '📚', label: 'Base conocim.' },
    { id: 'webhooks',       icon: '🔗', label: 'Webhooks' },
    { id: 'stats',          icon: '📊', label: 'Estadísticas' },
  ]

  return (
    <div className="page" style={{ maxWidth: '100%', padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Header */}
      <div style={{
        padding: isMobile ? '14px 16px 12px' : '20px 24px 16px', borderBottom: '1px solid var(--border)',
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? 10 : 0, flexShrink: 0,
      }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📱 Números de WhatsApp</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
              background: '#22c55e22',
              color: '#22c55e',
              border: '1px solid #22c55e66',
              letterSpacing: '0.03em', textTransform: 'uppercase', flexShrink: 0,
            }}>
              🔌 WAHA
            </span>
            <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: 13, whiteSpace: 'nowrap' }}>
              {sesiones.length} número(s) · {sesiones.filter(s => s.status === 'conectado').length} conectado(s)
            </p>
          </div>
        </div>
        <button
          className={`btn btn-primary${isMobile ? ' btn-sm' : ''}`}
          onClick={() => setShowNuevo(true)}
          style={isMobile ? { alignSelf: 'flex-start' } : undefined}
        >
          + Agregar número
        </button>
      </div>

      {/* Layout: lista + panel */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Lista de sesiones ── */}
        {/* En mobile con un número abierto, la lista se oculta (panel full-screen) */}
        <div style={{
          width: isMobile ? '100%' : (editando ? 320 : '100%'),
          display: isMobile && editando ? 'none' : 'block',
          flexShrink: 0, borderRight: (!isMobile && editando) ? '1px solid var(--border)' : 'none',
          overflowY: 'auto', padding: 16, transition: 'width .2s',
        }}>
          {sesiones.length === 0 && (
            <div className="empty-state" style={{ marginTop: 60 }}>
              <div className="empty-state-icon">📱</div>
              <p>Aún no tienes números conectados</p>
              <button className="btn btn-primary" onClick={() => setShowNuevo(true)} style={{ marginTop: 12 }}>+ Conectar primer número</button>
            </div>
          )}

          {sesiones.map(s => {
            const isActive = editando?.id === s.id
            return (
              <div
                key={s.id}
                onClick={() => abrir(s)}
                style={{
                  border: `2px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 14, padding: '14px 16px', marginBottom: 10, cursor: 'pointer',
                  background: isActive ? 'var(--bg-active)' : 'var(--bg-card)',
                  transition: 'border-color .15s, background .15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Avatar con color */}
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, background: s.color || '#25D366',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: 18, fontWeight: 700, flexShrink: 0,
                  }}>
                    {(s.nombre || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{s.nombre}</span>
                      {s.esPrincipal && <span style={{ fontSize: 10, background: '#d9770622', color: '#d97706', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>PRINCIPAL</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                      {s.telefono ? `+${s.telefono}` : 'Sin número'}
                    </div>
                  </div>
                  {/* Status badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLOR[s.status] || '#94a3b8' }} />
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{STATUS_LABEL[s.status] || s.status}</span>
                  </div>
                </div>

                {/* QR en la tarjeta si conectando y no hay panel abierto */}
                {!editando && s.qr && (
                  <div style={{ marginTop: 12, textAlign: 'center' }}>
                    <img src={s.qr} alt="QR" style={{ width: 180, height: 180, borderRadius: 8 }} />
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>Escanea con WhatsApp</div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* ── Panel de edición ── */}
        {editando && (
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

            {/* Panel header */}
            <div style={{ padding: isMobile ? '12px 14px' : '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexShrink: 0 }}>
              {/* Botón volver (solo mobile) */}
              {isMobile && (
                <button className="btn btn-ghost btn-icon" onClick={cerrar} style={{ flexShrink: 0, fontSize: 22, padding: '4px 8px', marginLeft: -4 }} aria-label="Volver">←</button>
              )}
              <div style={{
                width: isMobile ? 40 : 48, height: isMobile ? 40 : 48, borderRadius: 12, background: form.color || '#25D366',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: isMobile ? 17 : 20, fontWeight: 700, flexShrink: 0,
              }}>
                {(form.nombre || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: isMobile ? 15 : 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{form.nombre}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {form.telefono ? `+${form.telefono}` : 'Sin número asignado'} ·&nbsp;
                  <span style={{ color: STATUS_COLOR[editando.status] }}>{STATUS_LABEL[editando.status]}</span>
                </div>
              </div>
              {tab !== 'bot' && tab !== 'webhooks' && tab !== 'flujos' && tab !== 'integraciones' && tab !== 'kb' && (
                <button className="btn btn-primary" onClick={guardar} disabled={guardando} style={{ flexShrink: 0, ...(isMobile ? { padding: '8px 12px', fontSize: 13 } : {}) }}>
                  {guardando ? '⏳...' : (isMobile ? '💾' : '💾 Guardar')}
                </button>
              )}
              {/* Cerrar con ✕ solo en desktop (en mobile se usa el ← ) */}
              {!isMobile && (
                <button className="btn btn-ghost btn-icon" onClick={cerrar} style={{ flexShrink: 0, fontSize: 18 }}>✕</button>
              )}
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, padding: isMobile ? '0 10px' : '0 24px', borderBottom: '1px solid var(--border)', flexShrink: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {TABS.filter(t => t.id !== 'webhooks' || puedeVerWebhooks).map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  style={{
                    padding: '10px 14px', border: 'none', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
                    borderBottom: `3px solid ${tab === t.id ? 'var(--primary)' : 'transparent'}`,
                    fontWeight: tab === t.id ? 700 : 500, background: 'transparent',
                    color: tab === t.id ? 'var(--primary)' : 'var(--text-soft)',
                  }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div style={{ padding: isMobile ? 14 : 24, flex: 1 }}>

              {/* ── INFO ── */}
              {tab === 'info' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div className="card" style={{ padding: 20 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Identidad del número</h3>
                    <div className="form-group">
                      <label className="form-label">Nombre identificativo</label>
                      <input className="input" value={form.nombre || ''} onChange={e => upd('nombre', e.target.value)} placeholder="Ej: Ventas, Soporte, Tienda..." />
                    </div>
                    <div className="form-group" style={{ marginTop: 16 }}>
                      <label className="form-label">Color de identificación</label>
                      <div style={{ marginTop: 6 }}>
                        <SelectorColor value={form.color || '#25D366'} onChange={c => upd('color', c)} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, padding: '14px 0', borderTop: '1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>Número principal</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Se prioriza este número para campañas y operaciones generales</div>
                      </div>
                      <div className={`toggle ${form.esPrincipal ? 'on' : ''}`} onClick={() => upd('esPrincipal', !form.esPrincipal)} />
                    </div>
                  </div>

                  {/* Dónde vive este número en el mundo físico */}
                  <div className="card" style={{ padding: isMobile ? 16 : 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 10, flexWrap: 'wrap' }}>
                      <h3 style={{ fontSize: 15, fontWeight: 700 }}>📱 Dónde está este WhatsApp</h3>
                      <button className="btn btn-sm" onClick={() => setShowCelulares(true)}>Gestionar celulares</button>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
                      Para tu control: en qué aparato está instalado este número y de qué tipo de WhatsApp es.
                    </p>

                    <div className="form-group">
                      <label className="form-label">Celular</label>
                      <select className="input" value={form.dispositivoId || ''} onChange={e => upd('dispositivoId', e.target.value)}>
                        <option value="">— Sin asignar —</option>
                        {celulares.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.nombre}{c.marca || c.modelo ? ` · ${[c.marca, c.modelo].filter(Boolean).join(' ')}` : ''}
                          </option>
                        ))}
                      </select>
                      {!celulares.length && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                          Todavía no registraste ningún celular. Pulsa «Gestionar celulares» para añadir el primero con su foto.
                        </div>
                      )}
                    </div>

                    {/* Ficha del celular elegido: la foto es el dato que de verdad
                        ayuda a identificar el aparato correcto de un vistazo. */}
                    {(() => {
                      const cel = celulares.find(c => c.id === form.dispositivoId)
                      if (!cel) return null
                      return (
                        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 14, padding: 12, background: 'var(--bg)', borderRadius: 12 }}>
                          <FotoCelular foto={cel.foto} size={64} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{cel.nombre}</div>
                            {(cel.marca || cel.modelo) && (
                              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{[cel.marca, cel.modelo].filter(Boolean).join(' ')}</div>
                            )}
                            {cel.ubicacion && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>📍 {cel.ubicacion}</div>}
                            {cel.responsable && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>👤 {cel.responsable}</div>}
                          </div>
                        </div>
                      )
                    })()}

                    <div className="form-group" style={{ marginTop: 18 }}>
                      <label className="form-label">Tipo de WhatsApp</label>
                      <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                        {[
                          { id: 'normal',   icon: '💬', label: 'WhatsApp normal' },
                          { id: 'business', icon: '🏪', label: 'WhatsApp Business' },
                        ].map(t => {
                          const activo = (form.tipoWhatsapp || 'normal') === t.id
                          return (
                            <button key={t.id} type="button" onClick={() => upd('tipoWhatsapp', t.id)}
                              style={{
                                flex: isMobile ? '1 1 100%' : '1 1 0', padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                                textAlign: 'left', fontSize: 13, fontWeight: activo ? 700 : 500,
                                border: `2px solid ${activo ? 'var(--primary)' : 'var(--border)'}`,
                                background: activo ? 'var(--primary-soft, rgba(37,211,102,.08))' : 'transparent',
                                color: activo ? 'var(--primary)' : 'var(--text-soft)',
                              }}>
                              {t.icon} {t.label}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Info de solo lectura */}
                  <div className="card" style={{ padding: isMobile ? 16 : 20 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Información técnica</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                      {[
                        { label: 'ID sesión', value: editando.id?.slice(0, 8) + '...', full: editando.id },
                        { label: 'Teléfono', value: editando.telefono ? `+${editando.telefono}` : 'Sin detectar' },
                        { label: 'Estado', value: STATUS_LABEL[editando.status] || editando.status },
                      ].map(({ label, value, full }) => (
                        <div key={label} style={{ background: 'var(--bg)', borderRadius: 10, padding: '10px 14px' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5 }}>{label}</div>
                          <div title={full} style={{ fontWeight: 600, marginTop: 4, fontSize: 13, fontFamily: full ? 'monospace' : 'inherit' }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Danger zone */}
                  <div className="card" style={{ padding: 20, border: '1px solid #fecaca' }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#ef4444', marginBottom: 8 }}>⚠️ Zona peligrosa</h3>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>Eliminar este número desconecta la sesión de WhatsApp y borra toda la configuración del bot asociada.</p>
                    <button className="btn btn-sm" style={{ color: '#ef4444', borderColor: '#fca5a5' }} onClick={() => eliminar(editando)}>
                      🗑️ Eliminar número
                    </button>
                  </div>
                </div>
              )}

              {/* ── CONEXIÓN ── */}
              {tab === 'conexion' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div className="card" style={{ padding: 20 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Estado de conexión</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                      <div style={{ width: 52, height: 52, borderRadius: '50%', background: STATUS_COLOR[editando.status] + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: STATUS_COLOR[editando.status] }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: STATUS_COLOR[editando.status] }}>{STATUS_LABEL[editando.status]}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {editando.status === 'conectado' ? `+${editando.telefono || '...'}`
                            : editando.status === 'conectando' ? 'Esperando que escanees el código QR'
                            : editando.status === 'baneado' ? 'Este número fue baneado por WhatsApp'
                            : 'No hay sesión activa'}
                        </div>
                      </div>
                    </div>

                    {/* Reconectar y revincular son cosas distintas: la primera
                        levanta la sesión conservando el número, la segunda lo
                        desvincula. Antes los cuatro botones hacían lo segundo. */}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {editando.status === 'conectado' ? (
                        <>
                          <button className="btn btn-outline" onClick={() => navigate(`/config-bot/${editando.id}`)}>⚙️ Configurar Bot</button>
                          <button className="btn" style={{ color: '#f59e0b', borderColor: '#fcd34d' }} onClick={() => reconectar(editando.id)}>↺ Forzar reconexión</button>
                        </>
                      ) : editando.status === 'conectando' ? (
                        <button className="btn btn-outline" onClick={() => reconectar(editando.id)}>🔄 Reintentar</button>
                      ) : (
                        <button className="btn btn-primary" onClick={() => reconectar(editando.id)}>
                          {editando.telefono ? '↺ Reconectar' : '🔗 Conectar'}
                        </button>
                      )}

                      {editando.telefono && (
                        <button
                          className="btn btn-outline"
                          style={{ color: 'var(--text-muted)' }}
                          onClick={() => revincular(editando)}
                          title="Desvincula el número actual y pide un QR nuevo"
                        >📱 Vincular otro número</button>
                      )}
                    </div>

                    {editando.telefono && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
                        <strong>Reconectar</strong> levanta la sesión sin tocar el número vinculado: no hace falta escanear nada.
                        Solo usa <strong>Vincular otro número</strong> si de verdad quieres cambiar el WhatsApp de este número.
                      </div>
                    )}
                  </div>

                  {/* QR */}
                  {editando.qr && (
                    <div className="card" style={{ padding: 20, textAlign: 'center' }}>
                      <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Código QR</h3>
                      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Abre WhatsApp en tu teléfono → Dispositivos vinculados → Vincular dispositivo</p>
                      <img src={editando.qr} alt="QR" style={{ width: 220, height: 220, borderRadius: 12, border: '4px solid var(--border)' }} />
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>El código se renueva automáticamente cada ~20 segundos</p>
                    </div>
                  )}
                </div>
              )}

              {/* ── PIPELINE & NEGOCIO ── */}
              {tab === 'pipeline' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div className="card" style={{ padding: 20 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Pipeline predeterminado</h3>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Los nuevos negocios creados desde este número usarán este pipeline por defecto.</p>
                    <select className="input" value={form.pipelineDefaultId || ''} onChange={e => upd('pipelineDefaultId', e.target.value || null)}>
                      <option value="">— Sin pipeline predeterminado —</option>
                      {opciones.pipelines.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                    {opciones.pipelines.length === 0 && (
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Aún no tienes pipelines. Créalos en la sección Pipeline.</p>
                    )}
                  </div>

                  <div className="card" style={{ padding: 20 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Negocio asignado</h3>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>El negocio define el contexto de IA y las APIs que usa el bot de este número.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                        border: `2px solid ${!form.unidadNegocioId ? 'var(--primary)' : 'var(--border)'}`,
                        background: !form.unidadNegocioId ? 'var(--bg-active)' : 'var(--bg-card)' }}>
                        <input type="radio" name="negocio" checked={!form.unidadNegocioId} onChange={() => upd('unidadNegocioId', null)} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>Sin negocio asignado</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Usa la configuración global de la empresa</div>
                        </div>
                      </label>
                      {opciones.negocios.map(n => (
                        <label key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                          border: `2px solid ${form.unidadNegocioId === n.id ? 'var(--primary)' : 'var(--border)'}`,
                          background: form.unidadNegocioId === n.id ? 'var(--bg-active)' : 'var(--bg-card)' }}>
                          <input type="radio" name="negocio" checked={form.unidadNegocioId === n.id} onChange={() => upd('unidadNegocioId', n.id)} />
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: n.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{n.emoji}</div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{n.nombre}</div>
                        </label>
                      ))}
                      {opciones.negocios.length === 0 && (
                        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Aún no tienes negocios. Créalos en la sección Negocios.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── META API ── */}
              {tab === 'meta' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div className="card" style={{ padding: 20 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>☁️ Meta WhatsApp Cloud API</h3>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
                      Opcional. Completa esto si usas la API oficial de Meta en lugar de WAHA.<br />
                      Obtén estos datos en <strong>developers.facebook.com</strong> → Tu app → WhatsApp.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div className="form-group">
                        <label className="form-label">Phone Number ID</label>
                        <input className="input" value={form.metaPhoneNumberId || ''} onChange={e => upd('metaPhoneNumberId', e.target.value)} placeholder="123456789012345" />
                        <div className="form-hint">ID del número de teléfono en Meta Business Suite</div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">WhatsApp Business Account ID (WABA ID)</label>
                        <input className="input" value={form.metaWabaId || ''} onChange={e => upd('metaWabaId', e.target.value)} placeholder="123456789012345" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Token de acceso permanente</label>
                        <input className="input" type="password"
                          value={form.metaToken?.startsWith('••••') ? '' : (form.metaToken || '')}
                          onChange={e => upd('metaToken', e.target.value)}
                          placeholder={editando.metaToken ? '••••••••  (vacío = no cambiar)' : 'EAAxxxxxxxxxx...'} />
                        <div className="form-hint">Token de sistema de larga duración (no de usuario)</div>
                      </div>
                    </div>
                    {(form.metaPhoneNumberId || form.metaWabaId) && (
                      <div style={{ marginTop: 16, padding: '10px 14px', background: '#22c55e22', border: '1px solid #22c55e66', borderRadius: 8 }}>
                        <div style={{ fontSize: 13, color: '#22c55e', fontWeight: 600 }}>✅ Meta API configurada</div>
                        <div style={{ fontSize: 12, color: '#22c55e', marginTop: 2, opacity: .85 }}>Este número puede enviar mensajes vía Meta Cloud API</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── CHATBOT ── */}
              {tab === 'bot' && (
                <ConfigBot sesionIdProp={editando.id} embedded />
              )}

              {/* ── FLUJOS ── */}
              {tab === 'flujos' && (
                <FlujosPanel sesionId={editando.id} />
              )}

              {/* ── INTEGRACIONES ── */}
              {tab === 'integraciones' && (
                <IntegracionesPanel sesionId={editando.id} />
              )}

              {/* ── BASE DE CONOCIMIENTO ── */}
              {tab === 'kb' && (
                <KbPanel sesionId={editando.id} />
              )}

              {/* ── WEBHOOKS ── */}
              {tab === 'webhooks' && (
                <WebhooksPanel sesionId={editando.id} puedeEditar={usuario?.rol === 'admin'} />
              )}

              {/* ── ESTADÍSTICAS ── */}
              {tab === 'stats' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {!stats ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      <div className="spinner" style={{ margin: '0 auto 12px' }} />
                      Cargando estadísticas...
                    </div>
                  ) : (
                    <>
                      <div>
                        <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 12 }}>Chats</h3>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <StatCard label="Total de chats" value={stats.totalChats} />
                          <StatCard label="Chats abiertos" value={stats.chatsAbiertos} />
                          <StatCard label="Cerrados (7 días)" value={stats.chatsCerrados7d} />
                        </div>
                      </div>
                      <div>
                        <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 12 }}>Mensajes</h3>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <StatCard label="Total de mensajes" value={stats.totalMensajes.toLocaleString()} />
                          <StatCard label="Últimos 30 días" value={stats.mensajes30d.toLocaleString()} sub="mensajes enviados y recibidos" />
                        </div>
                      </div>
                      <div className="card" style={{ padding: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>Configuración del Bot</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Horario, respuestas automáticas, contexto de IA...</div>
                          </div>
                          <button className="btn btn-primary btn-sm" onClick={() => navigate(`/config-bot/${editando.id}`)}>⚙️ Abrir Bot</button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

            </div>
          </div>
        )}
      </div>

      {/* ── Modal nuevo número ── */}
      {showNuevo && (
        <div className="modal-overlay" onClick={() => setShowNuevo(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <span className="modal-title">📱 Nuevo número de WhatsApp</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowNuevo(false)}>✕</button>
            </div>
            <form onSubmit={crear}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Nombre identificativo</label>
                  <input className="input" value={nuevo.nombre} onChange={e => setNuevo({ ...nuevo, nombre: e.target.value })} placeholder="Ej: Ventas, Soporte, Mikus..." required autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Color</label>
                  <SelectorColor value={nuevo.color} onChange={c => setNuevo({ ...nuevo, color: c })} />
                </div>
                <div style={{ padding: '10px 14px', background: 'var(--primary)22', border: '1px solid var(--primary)', borderRadius: 8 }}>
                  <div style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 600 }}>ℹ️ Qué pasará</div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 4 }}>Se creará el número y se generará un código QR para vincularlo con WhatsApp.</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setShowNuevo(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={creando}>{creando ? '⏳ Creando...' : '✅ Crear y generar QR'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCelulares && (
        <CelularesModal
          celulares={celulares}
          isMobile={isMobile}
          onCerrar={() => setShowCelulares(false)}
          onCambio={cargarCelulares}
        />
      )}
    </div>
  )
}

// ── Celulares (dispositivos) ─────────────────────────────────────────────────

/** Las fotos se guardan como ruta relativa del backend; en el APK no hay
 *  "mismo dominio", así que hay que anteponerlo igual que con el resto de media. */
function urlFoto(foto) {
  if (!foto) return null
  if (foto.startsWith('http')) return foto
  return `${process.env.REACT_APP_BACKEND_URL || ''}${foto}`
}

function FotoCelular({ foto, size = 56 }) {
  const url = urlFoto(foto)
  return (
    <div style={{
      width: size, height: size, borderRadius: 12, flexShrink: 0, overflow: 'hidden',
      background: 'var(--bg-soft, #f1f5f9)', border: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.45, color: 'var(--text-muted)',
    }}>
      {url
        ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : '📱'}
    </div>
  )
}

const CELULAR_VACIO = { nombre: '', marca: '', modelo: '', ubicacion: '', responsable: '', numeroSim: '', notas: '' }

function CelularesModal({ celulares, isMobile, onCerrar, onCambio }) {
  const [editando, setEditando] = useState(null)   // null = lista; objeto = ficha
  const [form, setForm] = useState(CELULAR_VACIO)
  const [guardando, setGuardando] = useState(false)
  const [subiendo, setSubiendo] = useState(false)

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const abrirNuevo = () => { setEditando({ id: null }); setForm(CELULAR_VACIO) }
  const abrirFicha = (c) => { setEditando(c); setForm({ ...CELULAR_VACIO, ...c }) }

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error('Ponle un nombre para reconocerlo')
    setGuardando(true)
    try {
      if (editando.id) await api.put(`/dispositivos/${editando.id}`, form)
      else {
        const { data } = await api.post('/dispositivos', form)
        // Se queda en la ficha recién creada para poder subirle la foto sin
        // tener que volver a entrar.
        setEditando(data)
        setForm({ ...CELULAR_VACIO, ...data })
      }
      await onCambio()
      toast.success('Celular guardado')
      if (editando.id) setEditando(null)
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo guardar')
    } finally { setGuardando(false) }
  }

  const subirFoto = async (file) => {
    if (!file) return
    if (!editando?.id) return toast.error('Guarda primero el celular y después súbele la foto')
    setSubiendo(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const { data } = await api.post(`/dispositivos/${editando.id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setForm(prev => ({ ...prev, foto: data.foto }))
      await onCambio()
      toast.success('Foto actualizada')
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo subir la foto')
    } finally { setSubiendo(false) }
  }

  const quitarFoto = async () => {
    if (!editando?.id) return
    try {
      await api.delete(`/dispositivos/${editando.id}/foto`)
      setForm(prev => ({ ...prev, foto: null }))
      await onCambio()
    } catch { toast.error('No se pudo quitar la foto') }
  }

  const eliminar = async (c) => {
    const cuantos = (c.numeros || []).length
    const aviso = cuantos
      ? `"${c.nombre}" tiene ${cuantos} número(s) asignado(s). Se quedarán sin celular asignado, pero NO se borran ni se desconectan.\n\n¿Eliminar la ficha?`
      : `¿Eliminar "${c.nombre}"?`
    if (!window.confirm(aviso)) return
    try {
      await api.delete(`/dispositivos/${c.id}`)
      await onCambio()
      setEditando(null)
      toast.success('Celular eliminado')
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo eliminar') }
  }

  return (
    <div className="modal-overlay" onClick={onCerrar}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '100%' }}>
        <div className="modal-header">
          <span className="modal-title">
            {editando ? (editando.id ? '📱 Editar celular' : '📱 Nuevo celular') : '📱 Mis celulares'}
          </span>
          <button className="btn btn-ghost btn-icon" onClick={onCerrar}>✕</button>
        </div>

        {/* ── Lista ── */}
        {!editando && (
          <>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '60vh', overflowY: 'auto' }}>
              {!celulares.length && (
                <div style={{ textAlign: 'center', padding: '28px 10px', color: 'var(--text-muted)', fontSize: 13 }}>
                  Aún no registraste ningún celular.<br />Añade uno para saber en qué aparato está cada número.
                </div>
              )}
              {celulares.map(c => (
                <div key={c.id} onClick={() => abrirFicha(c)}
                  style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12, borderRadius: 12, cursor: 'pointer', border: '1px solid var(--border)' }}>
                  <FotoCelular foto={c.foto} size={52} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{c.nombre}</div>
                    {(c.marca || c.modelo) && (
                      <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{[c.marca, c.modelo].filter(Boolean).join(' ')}</div>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {(c.numeros || []).length
                        ? (c.numeros || []).map(n => n.nombre).join(' · ')
                        : 'Sin números asignados'}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {(c.numeros || []).length} 📞
                  </span>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={onCerrar}>Cerrar</button>
              <button className="btn btn-primary" onClick={abrirNuevo}>➕ Añadir celular</button>
            </div>
          </>
        )}

        {/* ── Ficha ── */}
        {editando && (
          <>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '62vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <FotoCelular foto={form.foto} size={84} />
                <div style={{ flex: 1 }}>
                  {editando.id ? (
                    <>
                      <label className="btn btn-sm" style={{ cursor: 'pointer', display: 'inline-block' }}>
                        {subiendo ? '⏳ Subiendo...' : (form.foto ? '🔄 Cambiar foto' : '📷 Subir foto')}
                        <input type="file" accept="image/*" hidden disabled={subiendo}
                          onChange={e => { subirFoto(e.target.files?.[0]); e.target.value = '' }} />
                      </label>
                      {form.foto && (
                        <button className="btn btn-sm" style={{ marginLeft: 8, color: '#ef4444' }} onClick={quitarFoto}>Quitar</button>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Guarda el celular y después podrás subirle la foto.
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="input" value={form.nombre} onChange={e => upd('nombre', e.target.value)}
                  placeholder="Ej: Celular de ventas, el de recepción..." autoFocus />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Marca</label>
                  <input className="input" value={form.marca || ''} onChange={e => upd('marca', e.target.value)} placeholder="Samsung, Xiaomi..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Modelo</label>
                  <input className="input" value={form.modelo || ''} onChange={e => upd('modelo', e.target.value)} placeholder="A54, Redmi Note 12..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Dónde está</label>
                  <input className="input" value={form.ubicacion || ''} onChange={e => upd('ubicacion', e.target.value)} placeholder="Oficina, tienda centro..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Quién lo tiene</label>
                  <input className="input" value={form.responsable || ''} onChange={e => upd('responsable', e.target.value)} placeholder="Nombre de la persona" />
                </div>
                <div className="form-group">
                  <label className="form-label">Línea / SIM</label>
                  <input className="input" value={form.numeroSim || ''} onChange={e => upd('numeroSim', e.target.value)} placeholder="Número de la SIM" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Notas</label>
                <textarea className="input" rows={3} value={form.notas || ''} onChange={e => upd('notas', e.target.value)}
                  placeholder="PIN, detalles del aparato, lo que necesites recordar..." />
              </div>

              {!!(editando.numeros || []).length && (
                <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Números en este celular</div>
                  {(editando.numeros || []).map(n => (
                    <div key={n.id} style={{ fontSize: 12, color: 'var(--text-soft)', display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 4, background: n.color || '#25D366', display: 'inline-block' }} />
                      {n.nombre}{n.telefono ? ` · +${n.telefono}` : ''}
                      {n.tipoWhatsapp === 'business' ? ' · Business' : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <div>
                {editando.id && (
                  <button className="btn btn-sm" style={{ color: '#ef4444', borderColor: '#fca5a5' }} onClick={() => eliminar(editando)}>🗑️ Eliminar</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={() => setEditando(null)}>Volver</button>
                <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
                  {guardando ? '⏳ Guardando...' : '💾 Guardar'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Panel de Integraciones por número ────────────────────────────────────────
function ApiKeyField({ label, hint, value, onChange, placeholder, isSet }) {
  const [visible, setVisible] = useState(false)
  return (
    <div className="form-group">
      <label className="form-label">
        {label}
        {isSet && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#22c55e', background: '#22c55e22', padding: '2px 8px', borderRadius: 20 }}>✓ Configurada</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input className="input" type={visible ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)}
          placeholder={isSet ? '••••••••••••  (dejar vacío para no cambiar)' : placeholder}
          style={{ paddingRight: 44 }} autoComplete="off" />
        <button type="button" onClick={() => setVisible(v => !v)}
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}>
          {visible ? '🙈' : '👁'}
        </button>
      </div>
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  )
}

function EstadoBadge({ estado }) {
  const map = {
    ok:             { color: '#22c55e', bg: '#22c55e22', icon: '✅', label: 'Conectado' },
    error:          { color: '#ef4444', bg: '#ef444422', icon: '❌', label: 'Error' },
    sincronizando:  { color: '#d97706', bg: '#d9770622', icon: '⏳', label: 'Sincronizando...' },
    sin_configurar: { color: 'var(--text-muted)', bg: 'var(--bg)', icon: '⚪', label: 'Sin configurar' },
  }
  const s = map[estado] || map.sin_configurar
  return (
    <span style={{ background: s.bg, color: s.color, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
      {s.icon} {s.label}
    </span>
  )
}

function IntegracionesPanel({ sesionId }) {
  const [cfg, setCfg] = useState(null)
  const [form, setForm] = useState({})
  const [stats, setStats] = useState(null)
  const [tab, setTab] = useState('alphastock')
  const [guardando, setGuardando] = useState(false)
  const [testando, setTestando] = useState(false)
  const [syncing, setSyncing] = useState(false)

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const cargar = useCallback(async () => {
    if (!sesionId) return
    try {
      const [cfgRes, statsRes] = await Promise.all([
        api.get(`/integraciones/${sesionId}`),
        api.get(`/integraciones/${sesionId}/alphastock/stats`).catch(() => ({ data: null })),
      ])
      setCfg(cfgRes.data)
      setForm({
        alphastockBaseUrl:       cfgRes.data.alphastockBaseUrl || '',
        alphastockApiKey:        '',
        alphastockWebhookSecret: '',
        alphastockActivo:        cfgRes.data.alphastockActivo || false,
        geminiApiKey:            '',
        geminiActivo:            cfgRes.data.geminiActivo || false,
      })
      setStats(statsRes.data)
    } catch { toast.error('Error cargando integraciones') }
  }, [sesionId])

  useEffect(() => { cargar() }, [cargar])

  const guardar = async () => {
    setGuardando(true)
    try {
      await api.put(`/integraciones/${sesionId}`, form)
      toast.success('✅ Configuración guardada')
      await cargar()
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar') }
    finally { setGuardando(false) }
  }

  const testConexion = async () => {
    setTestando(true)
    try {
      const { data } = await api.post(`/integraciones/${sesionId}/alphastock/test`)
      toast.success(`✅ Conectado · ${data.store?.name || 'OK'} · ${data.totalProductos} productos`)
      await cargar()
    } catch (e) {
      toast.error(`❌ ${e.response?.data?.error || 'Error de conexión'}`)
      await cargar()
    } finally { setTestando(false) }
  }

  const syncManual = async () => {
    setSyncing(true)
    try {
      await api.post(`/integraciones/${sesionId}/alphastock/sync`)
      toast.success('⚡ Sync iniciado')
      setTimeout(async () => { await cargar(); setSyncing(false) }, 4000)
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); setSyncing(false) }
  }

  if (!cfg) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Cargando integraciones…</div>

  const ITABS = [
    { id: 'alphastock', icon: '📦', label: 'AlphaStock' },
    { id: 'gemini',     icon: '🤖', label: 'Gemini IA' },
  ]

  return (
    <div style={{ maxWidth: 720 }}>
      {/* Tabs internos */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
        {ITABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '7px 16px', borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer', fontWeight: tab === t.id ? 700 : 500, fontSize: 13,
              background: tab === t.id ? 'var(--primary)' : 'transparent', color: tab === t.id ? 'white' : 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: 5 }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ═══ AlphaStock ═══ */}
      {tab === 'alphastock' && (
        <div>
          <div style={{ background: 'color-mix(in srgb, var(--primary-light) 10%, var(--bg-card))', border: '1px solid var(--primary-light)', borderRadius: 14, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ width: 46, height: 46, background: 'linear-gradient(135deg, #25D366, #128C7E)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>📦</div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>AlphaStock</div>
              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Inventario y stock en tiempo real</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <EstadoBadge estado={cfg.alphastockEstado} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Activo</span>
                <div className={`toggle ${form.alphastockActivo ? 'on' : ''}`} onClick={() => upd('alphastockActivo', !form.alphastockActivo)} />
              </div>
            </div>
          </div>

          {stats?.total > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Total productos', val: stats.total, color: '#3b82f6', bg: '#3b82f622' },
                { label: 'Disponibles', val: stats.disponible, color: '#22c55e', bg: '#22c55e22' },
                { label: 'Sin stock', val: stats.agotado, color: '#ef4444', bg: '#ef444422' },
              ].map(s => (
                <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '12px 14px', border: `1px solid ${s.color}22` }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="card" style={{ marginBottom: 12 }}>
            <h3 className="card-title">🔑 Credenciales</h3>
            <div className="form-group">
              <label className="form-label">URL Base de la API</label>
              <input className="input" value={form.alphastockBaseUrl} onChange={e => upd('alphastockBaseUrl', e.target.value)} placeholder="https://..." />
            </div>
            <ApiKeyField label="API Key" hint="Bearer Token para todas las llamadas." value={form.alphastockApiKey} onChange={v => upd('alphastockApiKey', v)} placeholder="eyJhbGci..." isSet={cfg.alphastockApiKeySet} />
            <ApiKeyField label="Webhook Secret" hint="Para validar webhooks de AlphaStock." value={form.alphastockWebhookSecret} onChange={v => upd('alphastockWebhookSecret', v)} placeholder="whsec_..." isSet={cfg.alphastockWebhookSecretSet} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={guardar} disabled={guardando}>{guardando ? '⏳ Guardando...' : '💾 Guardar'}</button>
              <button className="btn" onClick={testConexion} disabled={testando || !cfg.alphastockApiKeySet}>{testando ? '⏳...' : '▶ Probar conexión'}</button>
              <button className="btn" onClick={syncManual} disabled={syncing || !cfg.alphastockApiKeySet}>{syncing ? '⏳ Sincronizando...' : '⚡ Sincronizar ahora'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Gemini ═══ */}
      {tab === 'gemini' && (
        <div>
          <div style={{ background: 'color-mix(in srgb, var(--primary) 10%, var(--bg-card))', border: '1px solid var(--primary)', borderRadius: 14, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 46, height: 46, background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🤖</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Google Gemini IA</div>
              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>API Key propia para este número</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Activo</span>
              <div className={`toggle ${form.geminiActivo ? 'on' : ''}`} onClick={() => upd('geminiActivo', !form.geminiActivo)} />
            </div>
          </div>

          <div className="card">
            <h3 className="card-title">🔑 API Key de Gemini</h3>
            <ApiKeyField label="Google Gemini API Key" hint="Obtén tu key gratis en aistudio.google.com → Get API Key." value={form.geminiApiKey} onChange={v => upd('geminiApiKey', v)} placeholder="AIzaSy..." isSet={cfg.geminiApiKeySet} />
            <div style={{ background: '#d9770622', border: '1px solid #d9770655', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text-soft)', marginBottom: 14 }}>
              Si no configuras una key aquí, el bot usará la key global de la empresa (si existe) o la del sistema CRM.
            </div>
            <button className="btn btn-primary" onClick={guardar} disabled={guardando}>{guardando ? '⏳ Guardando...' : '💾 Guardar'}</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Panel de Flujos por número ────────────────────────────────────────────────
function FlujosPanel({ sesionId }) {
  const navigate = useNavigate()
  const [flujos, setFlujos] = useState([])
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    if (!sesionId) return
    setCargando(true)
    api.get('/bot/flujos').then(r => {
      setFlujos((r.data || []).filter(f => f.sesionId === sesionId))
    }).catch(() => toast.error('Error cargando flujos')).finally(() => setCargando(false))
  }, [sesionId])

  const crear = async () => {
    try {
      const res = await api.post('/bot/flujos', {
        nombre: 'Nuevo flujo',
        sesionId,
        trigger: 'palabra_clave',
        palabrasClave: [],
        nodos: [{ id: 'n-inicio', tipo: 'inicio', position: { x: 120, y: 80 }, config: { palabrasClave: [], modoCoincidencia: 'contiene' } }],
        conexiones: [],
      })
      navigate(`/flujos/${res.data.id}`)
    } catch { toast.error('Error creando flujo') }
  }

  const toggleActivo = async (flujo) => {
    try {
      await api.put(`/bot/flujos/${flujo.id}`, { activo: !flujo.activo })
      setFlujos(fs => fs.map(f => f.id === flujo.id ? { ...f, activo: !f.activo } : f))
    } catch { toast.error('Error actualizando flujo') }
  }

  const eliminar = async (flujoId) => {
    if (!window.confirm('¿Eliminar este flujo?')) return
    try {
      await api.delete(`/bot/flujos/${flujoId}`)
      setFlujos(fs => fs.filter(f => f.id !== flujoId))
      toast.success('Flujo eliminado')
    } catch { toast.error('Error eliminando') }
  }

  if (cargando) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Cargando flujos…</div>

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Flujos de automatización</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>Respuestas automáticas por palabras clave con delays naturales</div>
        </div>
        <button className="btn btn-primary" style={{ flexShrink: 0 }} onClick={crear}>+ Nuevo flujo</button>
      </div>

      {flujos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🤖</div>
          <div style={{ fontSize: 14, marginBottom: 4 }}>No hay flujos para este número</div>
          <div style={{ fontSize: 12 }}>Crea un flujo para responder automáticamente con texto, imágenes y documentos</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {flujos.map(f => (
            <div key={f.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{f.nombre}</span>
                  <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: f.activo ? 'rgba(34,197,94,0.12)' : 'var(--bg)', color: f.activo ? '#22c55e' : 'var(--text-muted)', border: `1px solid ${f.activo ? '#22c55e44' : 'var(--border)'}` }}>
                    {f.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                {f.palabrasClave?.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                    {f.palabrasClave.map((k, i) => (
                      <span key={i} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#312e81', color: '#a5b4fc' }}>{k}</span>
                    ))}
                  </div>
                )}
                <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                  {f.nodos?.length || 0} nodos · {f.vecesEjecutado || 0} ejecuciones
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => toggleActivo(f)} title={f.activo ? 'Desactivar' : 'Activar'}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14 }}>
                  {f.activo ? '⏸' : '▶'}
                </button>
                <button className="btn btn-primary" onClick={() => navigate(`/flujos/${f.id}`)} style={{ fontSize: 12, padding: '6px 14px' }}>
                  Editar
                </button>
                <button onClick={() => eliminar(f.id)}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #7f1d1d', background: 'transparent', color: '#f87171', cursor: 'pointer', fontSize: 14 }}>
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Panel de Base de Conocimiento por número ──────────────────────────────────
function KbPanel({ sesionId }) {
  const [items, setItems] = useState([])
  const [cargando, setCargando] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editando, setEditando] = useState(null)
  const [form, setForm] = useState({ titulo: '', categoria: '', contenido: '', activo: true })

  const cargar = async () => {
    if (!sesionId) return
    setCargando(true)
    try {
      const { data } = await api.get('/bot/kb', { params: { sesionId } })
      setItems(data || [])
    } catch { toast.error('Error cargando base de conocimiento') }
    finally { setCargando(false) }
  }
  useEffect(() => { cargar() }, [sesionId])

  const abrirNuevo = () => { setEditando(null); setForm({ titulo: '', categoria: '', contenido: '', activo: true }); setShowModal(true) }
  const editar = (item) => { setEditando(item); setForm(item); setShowModal(true) }

  const guardar = async (e) => {
    e.preventDefault()
    try {
      if (editando) await api.put(`/bot/kb/${editando.id}`, form)
      else await api.post('/bot/kb', { ...form, sesionId })
      toast.success('Guardado')
      setShowModal(false); setEditando(null)
      cargar()
    } catch (e) { toast.error(e.response?.data?.error || 'Error') }
  }

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar?')) return
    try { await api.delete(`/bot/kb/${id}`); cargar() } catch { toast.error('Error eliminando') }
  }

  if (cargando) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Cargando base de conocimiento…</div>

  const porCategoria = items.reduce((acc, it) => {
    const k = it.categoria || 'Sin categoría'
    acc[k] = acc[k] || []
    acc[k].push(it)
    return acc
  }, {})

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Base de conocimiento</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>Información que el bot de este número usará para responder con precisión</div>
        </div>
        <button className="btn btn-primary" style={{ flexShrink: 0 }} onClick={abrirNuevo}>+ Agregar</button>
      </div>

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📚</div>
          <div style={{ fontSize: 14, marginBottom: 4 }}>Sin entradas para este número</div>
          <div style={{ fontSize: 12 }}>Agrega información sobre productos, precios, políticas, FAQ, etc.</div>
        </div>
      ) : (
        Object.entries(porCategoria).map(([cat, list]) => (
          <div key={cat} style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase' }}>{cat}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {list.map(item => (
                <div key={item.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{item.titulo}</span>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: item.activo ? 'rgba(34,197,94,0.12)' : 'var(--bg)', color: item.activo ? '#22c55e' : 'var(--text-muted)', border: `1px solid ${item.activo ? '#22c55e44' : 'var(--border)'}` }}>
                      {item.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', maxHeight: 60, overflow: 'hidden', marginBottom: 10 }}>{item.contenido}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => editar(item)}>✏️ Editar</button>
                    <button onClick={() => eliminar(item.id)}
                      style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #7f1d1d', background: 'transparent', color: '#f87171', cursor: 'pointer', fontSize: 12 }}>
                      🗑 Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editando ? 'Editar' : 'Nueva'} entrada</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={guardar}>
              <div className="modal-body">
                <div className="form-group"><label className="form-label">Título</label><input className="input" required value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} placeholder="Política de devoluciones" /></div>
                <div className="form-group"><label className="form-label">Categoría</label><input className="input" value={form.categoria || ''} onChange={e => setForm({ ...form, categoria: e.target.value })} placeholder="productos, faq, políticas..." /></div>
                <div className="form-group"><label className="form-label">Contenido</label><textarea className="textarea" rows={8} required value={form.contenido} onChange={e => setForm({ ...form, contenido: e.target.value })} placeholder="Información detallada que el bot usará para responder..." /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setShowModal(false)}>Cancelar</button>
                <button className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Panel de Webhooks por número ──────────────────────────────────────────────
const EVENTOS_DISPONIBLES = [
  { id: 'mensaje_recibido',    label: 'Mensaje recibido' },
  { id: 'mensaje_enviado_bot', label: 'Mensaje enviado por bot' },
  { id: 'contacto_creado',     label: 'Contacto creado' },
  { id: 'negocio_movido',      label: 'Negocio movido de etapa' },
  { id: 'chat_escalado',       label: 'Chat escalado a humano' },
]

const WH_VACIO = () => ({ nombre: '', url: '', eventos: [], secret: '', activo: true })

function generarSecretWebhookSesion() {
  const bytes = new Uint8Array(32)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

function WebhooksPanel({ sesionId, puedeEditar }) {
  const [webhooks, setWebhooks] = useState([])
  const [cargando, setCargando] = useState(true)
  const [editandoWH, setEditandoWH] = useState(null)
  const [form, setForm] = useState(WH_VACIO())
  const [guardando, setGuardando] = useState(false)
  const [secretUnaVez, setSecretUnaVez] = useState(null)

  const cargar = useCallback(async () => {
    setCargando(true)
    try { const { data } = await api.get(`/sesiones/${sesionId}/webhooks`); setWebhooks(data) }
    catch { toast.error('Error cargando webhooks') }
    finally { setCargando(false) }
  }, [sesionId])

  useEffect(() => { cargar() }, [cargar])

  const abrirNuevo = () => { setForm(WH_VACIO()); setEditandoWH('nuevo') }
  const abrirEditar = (wh) => {
    setForm({
      nombre: wh.nombre || '',
      url: wh.url || '',
      eventos: Array.isArray(wh.eventos) ? wh.eventos : [],
      activo: wh.activo !== false,
      secret: '',
    })
    setEditandoWH(wh.id)
  }
  const cerrarForm = () => { setEditandoWH(null); setForm(WH_VACIO()) }

  const guardar = async () => {
    if (!form.nombre.trim() || !form.url.trim()) return toast.error('Nombre y URL son requeridos')
    if (editandoWH === 'nuevo' && form.secret.length < 32) return toast.error('El secret debe tener al menos 32 caracteres')
    if (editandoWH !== 'nuevo' && form.secret && form.secret.length < 32) return toast.error('El secret debe tener al menos 32 caracteres')
    setGuardando(true)
    try {
      if (editandoWH === 'nuevo') {
        const payload = {
          nombre: form.nombre,
          url: form.url,
          eventos: form.eventos,
          activo: form.activo,
          secret: form.secret,
        }
        const { data } = await api.post(`/sesiones/${sesionId}/webhooks`, payload)
        setWebhooks(prev => [...prev, data])
        setSecretUnaVez({ secret: form.secret, titulo: 'Webhook creado: guarda el secret' })
        toast.success('Webhook creado. Guarda el secret mostrado antes de continuar.')
      } else {
        const body = {
          nombre: form.nombre,
          url: form.url,
          eventos: form.eventos,
          activo: form.activo,
        }
        if (form.secret) body.secret = form.secret
        const { data } = await api.put(`/sesiones/${sesionId}/webhooks/${editandoWH}`, body)
        setWebhooks(prev => prev.map(w => w.id === editandoWH ? data : w))
        if (form.secret) {
          setSecretUnaVez({ secret: form.secret, titulo: 'Secret actualizado: guarda el nuevo valor' })
          toast.success('Webhook actualizado. Guarda el nuevo secret antes de continuar.')
        } else {
          toast.success('Webhook actualizado')
        }
      }
      cerrarForm()
    } catch (e) { toast.error(e.response?.data?.error || 'Error') }
    finally { setGuardando(false) }
  }

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este webhook?')) return
    await api.delete(`/sesiones/${sesionId}/webhooks/${id}`)
    setWebhooks(prev => prev.filter(w => w.id !== id))
    toast.success('Webhook eliminado')
  }

  const toggleEvento = (ev) => {
    setForm(prev => ({
      ...prev,
      eventos: prev.eventos.includes(ev) ? prev.eventos.filter(e => e !== ev) : [...prev.eventos, ev],
    }))
  }

  if (cargando) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}><div className="spinner" style={{ margin: '0 auto 12px' }} />Cargando webhooks...</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {secretUnaVez && (
        <OneTimeSecretDialog
          secret={secretUnaVez.secret}
          titulo={secretUnaVez.titulo}
          onConfirm={() => setSecretUnaVez(null)}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Webhooks de este número</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Notificaciones HTTP independientes para este canal de WhatsApp</div>
        </div>
        {puedeEditar && <button className="btn btn-primary btn-sm" onClick={abrirNuevo}>+ Agregar webhook</button>}
      </div>

      {webhooks.length === 0 && !editandoWH && (
        <div className="empty-state" style={{ padding: 40 }}>
          <div className="empty-state-icon">🔗</div>
          <p>No hay webhooks para este número</p>
          {puedeEditar && <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={abrirNuevo}>+ Crear webhook</button>}
        </div>
      )}

      {webhooks.map(wh => (
        <div key={wh.id} className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{wh.nombre}</span>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 700,
                  background: wh.activo ? '#22c55e22' : '#94a3b822', color: wh.activo ? '#22c55e' : '#94a3b8' }}>
                  {wh.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace', marginBottom: 6, wordBreak: 'break-all' }}>{wh.url}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(wh.eventos || []).map(ev => (
                  <span key={ev} style={{ fontSize: 11, background: 'var(--primary)22', color: 'var(--primary)', padding: '2px 8px', borderRadius: 20 }}>
                    {EVENTOS_DISPONIBLES.find(e => e.id === ev)?.label || ev}
                  </span>
                ))}
              </div>
              {(wh.totalDisparos > 0 || wh.totalFallos > 0) && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  {wh.totalDisparos} disparos · {wh.totalFallos} fallos
                  {wh.ultimoDisparo && ` · último: ${new Date(wh.ultimoDisparo).toLocaleDateString()}`}
                </div>
              )}
            </div>
            <div style={{ display: puedeEditar ? 'flex' : 'none', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-sm btn-ghost" onClick={() => abrirEditar(wh)}>✏️</button>
              <button className="btn btn-sm btn-ghost" style={{ color: '#ef4444' }} onClick={() => eliminar(wh.id)}>🗑️</button>
            </div>
          </div>
        </div>
      ))}

      {/* Formulario inline */}
      {editandoWH && (
        <div className="card" style={{ padding: 20, border: '2px solid var(--primary)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
            {editandoWH === 'nuevo' ? '+ Nuevo webhook' : '✏️ Editar webhook'}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Nombre</label>
              <input className="input" value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Notificar sistema de ventas" />
            </div>
            <div className="form-group">
              <label className="form-label">URL destino</label>
              <input className="input" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://mi-sistema.com/webhook" />
            </div>
            <div className="form-group">
              <label className="form-label">Eventos que disparan este webhook</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                {EVENTOS_DISPONIBLES.map(ev => (
                  <label key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.eventos.includes(ev.id)} onChange={() => toggleEvento(ev.id)} />
                    <span style={{ fontSize: 13 }}>{ev.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Secret (firma HMAC-SHA256)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="input" type="password" value={form.secret} onChange={e => setForm(p => ({ ...p, secret: e.target.value }))}
                  placeholder={editandoWH !== 'nuevo' ? 'Vacío = no cambiar' : 'Mínimo 32 caracteres'} />
                <button type="button" className="btn btn-ghost" onClick={() => {
                  const secret = generarSecretWebhookSesion()
                  setForm(p => ({ ...p, secret }))
                  toast.success('Secret generado. Guárdalo antes de guardar el webhook.')
                }}>Generar</button>
              </div>
              <div className="form-hint">Guárdalo antes de continuar; el CRM no volverá a mostrarlo.</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 4, borderTop: '1px solid var(--border)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.activo} onChange={e => setForm(p => ({ ...p, activo: e.target.checked }))} />
                <span style={{ fontSize: 13 }}>Webhook activo</span>
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={cerrarForm}>Cancelar</button>
                <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
                  {guardando ? '⏳...' : '💾 Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
